import React, { useState, useEffect, useRef } from "react";
import { memberFetch } from "../../lib/memberApi";
import { toast } from "sonner";
import { Camera, Upload, AlertCircle, Loader2 } from "lucide-react";
import { fmtDate } from "./_shared";

export default function ProgressTab({ me, refreshMe }) {
  const [checkIns, setCheckIns] = useState([]);
  const [form, setForm] = useState({ weight_lb: "", height_in: "", body_fat_pct: "", notes: "" });
  const [photos, setPhotos] = useState({ photo_front: null, photo_back: null, photo_side: null });
  const [saving, setSaving] = useState(false);
  const frontRef = useRef(); const backRef = useRef(); const sideRef = useRef();

  useEffect(() => { load(); }, []);
  const load = () => memberFetch("/member/check-ins").then(setCheckIns).catch(() => setCheckIns([]));

  const onPhoto = (key, file) => {
    if (!file) return;
    if (file.size > 2_500_000) { toast.error(`${key} image too large (max 2.5MB)`); return; }
    const r = new FileReader();
    r.onload = () => setPhotos((p) => ({ ...p, [key]: r.result }));
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        weight_lb: form.weight_lb ? Number(form.weight_lb) : null,
        height_in: form.height_in ? Number(form.height_in) : null,
        body_fat_pct: form.body_fat_pct ? Number(form.body_fat_pct) : null,
        notes: form.notes || null,
        ...photos,
      };
      await memberFetch("/member/check-in", { method: "POST", body: JSON.stringify(body) });
      toast.success("Check-in saved");
      setForm({ weight_lb: "", height_in: "", body_fat_pct: "", notes: "" });
      setPhotos({ photo_front: null, photo_back: null, photo_side: null });
      if (frontRef.current) frontRef.current.value = "";
      if (backRef.current) backRef.current.value = "";
      if (sideRef.current) sideRef.current.value = "";
      await load();
      if (refreshMe) refreshMe();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const last = checkIns[0];

  return (
    <div data-testid="tab-progress">
      <h2 className="font-display text-3xl mb-2">Goal Tracking</h2>
      <p className="text-zinc-500 text-sm mb-6">Log a check-in every 30 days. Photos + measurements compound — even tiny changes show up over months.</p>

      {me?.due_for_checkin && (
        <div className="border border-[#E5B43A] bg-[#E5B43A]/10 p-4 mb-6 flex items-start gap-3" data-testid="checkin-reminder">
          <AlertCircle className="text-[#E5B43A] shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-display text-lg mb-1">Check-in Due</div>
            <p className="text-sm text-zinc-200">
              {last
                ? `It's been ${me.days_since_checkin} day${me.days_since_checkin === 1 ? "" : "s"} since your last check-in. Drop today's numbers below.`
                : "Add your first baseline check-in below — photos + measurements."}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="border border-zinc-800 bg-[#141414] p-6 md:p-8 mb-10" data-testid="checkin-form">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-4">New Check-In</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Height (in)</label>
            <input type="number" step="0.1" value={form.height_in} onChange={(e) => setForm({ ...form, height_in: e.target.value })} className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 text-white focus:outline-none focus:border-[#E5B43A]" data-testid="height-input" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Weight (lb)</label>
            <input type="number" step="0.1" value={form.weight_lb} onChange={(e) => setForm({ ...form, weight_lb: e.target.value })} className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 text-white focus:outline-none focus:border-[#E5B43A]" data-testid="weight-input" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Body Fat %</label>
            <input type="number" step="0.1" value={form.body_fat_pct} onChange={(e) => setForm({ ...form, body_fat_pct: e.target.value })} className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 text-white focus:outline-none focus:border-[#E5B43A]" data-testid="bf-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { key: "photo_front", label: "Front", ref: frontRef },
            { key: "photo_back", label: "Back", ref: backRef },
            { key: "photo_side", label: "Side", ref: sideRef },
          ].map(({ key, label, ref }) => (
            <div key={key} className="border border-zinc-800 bg-[#0F0F0F]">
              <div className="aspect-[3/4] bg-[#0A0A0A] grid place-items-center overflow-hidden">
                {photos[key] ? (
                  <img src={photos[key]} alt={label} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-zinc-600">
                    <Camera size={28} className="mx-auto mb-2" />
                    <div className="text-[10px] tracking-widest uppercase">{label}</div>
                  </div>
                )}
              </div>
              <label className="flex items-center justify-center gap-2 p-3 cursor-pointer hover:bg-[#1a1a1a] text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-[#E5B43A]">
                <Upload size={12} /> {photos[key] ? "Replace" : "Upload"} {label}
                <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(key, e.target.files?.[0])} data-testid={`upload-${key}`} />
              </label>
            </div>
          ))}
        </div>

        <textarea
          rows={3}
          placeholder="Notes (energy, soreness, sleep, food adherence...)"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 mb-4 text-white resize-none focus:outline-none focus:border-[#E5B43A]"
          data-testid="checkin-notes"
        />
        <button type="submit" disabled={saving} data-testid="save-checkin-btn" className="bg-[#E5B43A] text-black px-8 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-60">
          {saving ? "Saving..." : "Save Check-In"}
        </button>
      </form>

      <h3 className="font-display text-2xl mb-4">History ({checkIns.length})</h3>
      {checkIns.length === 0 ? (
        <p className="text-zinc-500 text-sm italic">No check-ins yet — log your baseline above.</p>
      ) : (
        <div className="space-y-4">
          {checkIns.map((c) => (
            <div key={c.id} className="border border-zinc-800 bg-[#141414] p-6" data-testid={`checkin-${c.id}`}>
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
                <div className="font-display text-xl">{fmtDate(c.created_at)}</div>
                <div className="flex gap-4 text-sm">
                  {c.weight_lb != null && <span><b>{c.weight_lb}</b> lb</span>}
                  {c.height_in != null && <span><b>{c.height_in}</b> in</span>}
                  {c.body_fat_pct != null && <span><b>{c.body_fat_pct}</b>%</span>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {["photo_front", "photo_back", "photo_side"].map((k) => (
                  <div key={k} className="aspect-[3/4] bg-[#0A0A0A] border border-zinc-800 grid place-items-center overflow-hidden">
                    {c[k] ? <img src={c[k]} alt={k} className="w-full h-full object-cover" /> : <Camera className="text-zinc-700" size={20} />}
                  </div>
                ))}
              </div>
              {c.notes && <p className="text-sm text-zinc-400 italic mt-2">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
